import React from 'react';

import { Icon, Text, TooltipWrapper } from '@zapier/design-system';

import DoubleDropdowns from 'app/developer-v3/components/DoubleDropdowns';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { incrementVersion } from 'app/developer-v3/utils';

import VersionModalWrapper from './VersionModalWrapper';

import type { SemVer } from 'app/developer-v3/types/version';

type Props = {
  fromVersion?: SemVer;
  isSaving: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onSetFromVersion?: (value: { value: SemVer }) => void;
  onSetToVersion: (value: { value: SemVer }) => void;
  toVersion?: SemVer;
  versions: Array<{ isGUIIntegration?: boolean; version: SemVer }>;
};

const Clone = ({
  fromVersion,
  isSaving,
  onClose,
  onConfirm,
  onSetFromVersion,
  onSetToVersion,
  toVersion,
  versions,
}: Props) => {
  const fromVersionObj = versions.find(v => v.version === fromVersion);

  const versionObjs = versions.map(({ isGUIIntegration, version }) => ({
    isDisabled: !isGUIIntegration,
    ...(isGUIIntegration
      ? {}
      : {
          iconAfter: (
            <TooltipWrapper
              allowMultilineTooltip={true}
              content={
                <div css={{ minWidth: '100px' }}>
                  CLI versions can't be cloned in the UI.
                </div>
              }
              position="east"
            >
              {childProps => (
                <Icon name="actionLockFill" size={16} {...childProps} />
              )}
            </TooltipWrapper>
          ),
        }),
    label: version,
    value: version,
  }));

  const existingVersions = versions.map(({ version }) => version);
  const incrementedVersionObjs = fromVersion
    ? incrementVersion(fromVersion, existingVersions)
    : [];

  return (
    <VersionModalWrapper
      confirmButtonDisabled={!fromVersionObj?.isGUIIntegration ?? false}
      confirmButtonLabel="Clone"
      description={
        <Text margin="10px 0px 20px 0px" tag="p" type="Body1">
          Create a copy of any version of this integration, and choose to have
          it be a patch, minor, or major version. You can then make changes to
          the cloned version without affecting existing users. Before making
          updates, it’s important to consider the potential{' '}
          <Link
            href={`${BASE_DOC_URL_VB}/manage/planning-changes`}
            target="_blank"
          >
            impact on user migration and existing Zaps
          </Link>
          .
        </Text>
      }
      heading="Clone Version"
      isSaving={isSaving}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <DoubleDropdowns
        leftDropdown={{
          title: 'Clone From Version',
          items: versionObjs,
          value: fromVersion,
          onSetValue: onSetFromVersion,
        }}
        rightDropdown={{
          title: 'Clone To Version',
          items: incrementedVersionObjs,
          value: toVersion,
          onSetValue: onSetToVersion,
        }}
      />
    </VersionModalWrapper>
  );
};

export default Clone;
